import { Employee, ContractType, HealthInsuranceCompany, EmployeeChild, EmployeeGarnishment } from '@/types';
import { ValidationResult } from '@/types/slovak-payroll';

/**
 * Utility functions for managing Slovak compliance in unified Employee system
 */

/**
 * Check if an employee has Slovak compliance enabled
 */
export const isSlovakCompliantEmployee = (employee: Employee): boolean => {
  return !!(employee.contract_type && employee.rodne_cislo);
};

/**
 * Get the completeness score of Slovak compliance data (0-100%)
 */
export const getSlovakComplianceCompleteness = (employee: Employee): number => {
  if (!employee.contract_type) return 0;
  
  const requiredFields = [
    'contract_type',
    'rodne_cislo',
    'contract_start_date',
    'weekly_hours',
    'health_insurance_company',
    'iban'
  ];
  
  const optionalButImportantFields = [
    'permanent_address',
    'applies_tax_free_allowance',
    'applies_child_tax_bonus'
  ];
  
  const allFields = [...requiredFields, ...optionalButImportantFields];
  let completedFields = 0;
  
  for (const field of allFields) {
    const value = employee[field as keyof Employee];
    if (value !== null && value !== undefined && value !== '') {
      completedFields++;
    }
  }
  
  return Math.round((completedFields / allFields.length) * 100);
};

/**
 * Convert unified Employee to EmployeeSlovak for payroll calculations
 */
export const employeeToSlovakEmployee = (employee: Employee): Employee => {
  return {
    // Base employee fields
    id: employee.id,
    business_id: employee.business_id,
    name: employee.name,
    email: employee.email,
    role_id: employee.role_id,
    salary_adjustment: employee.salary_adjustment,
    user_id: employee.user_id,
    
    // Include pay rate information
    hourly_rate: employee.hourly_rate,
    roleInfo: employee.roleInfo,
    employmentTypeInfo: employee.employmentTypeInfo,
    
    // Slovak-specific fields with defaults
    rodne_cislo: employee.rodne_cislo || '',
    id_card_number: employee.id_card_number || '',
    permanent_address: employee.permanent_address || '',
    contract_type: employee.contract_type as ContractType || 'TPP',
    contract_start_date: employee.contract_start_date || new Date().toISOString().split('T')[0],
    contract_end_date: employee.contract_end_date,
    weekly_hours: employee.weekly_hours || 40,
    health_insurance_company: employee.health_insurance_company as HealthInsuranceCompany || 'VšZP',
    is_pensioner: employee.is_pensioner || false,
    is_student: employee.is_student || false,
    is_disabled: employee.is_disabled || false,
    has_severe_disability: employee.has_severe_disability || false,
    applies_tax_free_allowance: employee.applies_tax_free_allowance || false,
    applies_child_tax_bonus: employee.applies_child_tax_bonus || false,
    applies_spouse_allowance: employee.applies_spouse_allowance || false,
    iban: employee.iban || '',
    bank_code: employee.bank_code,
    third_pillar_contribution: employee.third_pillar_contribution,
    union_contribution: employee.union_contribution,
    has_garnishments: employee.has_garnishments || false,
    
    // Related data
    children: employee.children || [],
    garnishments: employee.garnishments || [],
    
    created_at: employee.created_at,
    updated_at: employee.updated_at
  };
};

/**
 * Validate Slovak employee data and return validation results
 */
export const validateSlovakEmployee = (employee: Employee): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!employee.contract_type) {
    errors.push('Contract type is required for Slovak compliance');
  }
  
  if (!employee.rodne_cislo) {
    errors.push('Rodné číslo (birth number) is required');
  } else if (employee.rodne_cislo && !isValidRodneCislo(employee.rodne_cislo)) {
    errors.push('Invalid rodné číslo format');
  }
  
  if (!employee.contract_start_date) {
    errors.push('Contract start date is required');
  }
  
  if (!employee.weekly_hours || employee.weekly_hours <= 0) {
    errors.push('Weekly hours must be greater than 0');
  }
  
  if (!employee.health_insurance_company) {
    errors.push('Health insurance company is required');
  }
  
  if (!employee.iban) {
    errors.push('IBAN is required for salary payments');
  } else if (employee.iban && !isValidSlovakIBAN(employee.iban)) {
    warnings.push('IBAN format may be invalid');
  }
  
  // Warnings for incomplete optional data
  if (!employee.permanent_address) {
    warnings.push('Permanent address is recommended for compliance');
  }
  
  if (employee.applies_child_tax_bonus && (!employee.children || employee.children.length === 0)) {
    warnings.push('Child tax bonus is enabled but no children are registered');
  }
  
  if (employee.has_garnishments && (!employee.garnishments || employee.garnishments.length === 0)) {
    warnings.push('Garnishments are enabled but no garnishments are defined');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Basic rodné číslo validation (Slovak birth number)
 */
export const isValidRodneCislo = (rc: string): boolean => {
  if (!rc) return false;
  
  // Remove slashes and spaces
  const cleaned = rc.replace(/[\/\s]/g, '');
  
  // Must be 9 or 10 digits
  if (!/^\d{9,10}$/.test(cleaned)) return false;
  
  // Basic format validation (YYMMDD/XXX or YYMMDD/XXXX)
  const year = parseInt(cleaned.substring(0, 2));
  const month = parseInt(cleaned.substring(2, 4));
  const day = parseInt(cleaned.substring(4, 6));
  
  // Month validation (can be +50 for women, +20 for extra capacity)
  const realMonth = month % 50 % 20;
  if (realMonth < 1 || realMonth > 12) return false;
  
  // Day validation
  if (day < 1 || day > 31) return false;
  
  // If 10 digits, check the checksum
  if (cleaned.length === 10) {
    const checksum = parseInt(cleaned.substring(9, 10));
    const number = parseInt(cleaned.substring(0, 9));
    if (number % 11 !== checksum && !(number % 11 === 10 && checksum === 0)) {
      return false;
    }
  }
  
  return true;
};

/**
 * Basic Slovak IBAN validation
 */
export const isValidSlovakIBAN = (iban: string): boolean => {
  if (!iban) return false;
  
  // Remove spaces and convert to uppercase
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  
  // Slovak IBAN format: SK + 2 check digits + 20 digits
  if (!/^SK\d{22}$/.test(cleaned)) return false;
  
  // Basic checksum validation (simplified)
  const rearranged = cleaned.substring(4) + cleaned.substring(0, 4);
  const numericString = rearranged.replace(/[A-Z]/g, (char) => {
    return (char.charCodeAt(0) - 55).toString();
  });
  
  // Simple mod 97 check (simplified version)
  try {
    const remainder = BigInt(numericString) % BigInt(97);
    return remainder === BigInt(1);
  } catch {
    return false; // If BigInt fails, assume invalid
  }
};

/**
 * Get Slovak contract type display name
 */
export const getContractTypeDisplayName = (contractType: ContractType, language: 'sk' | 'en' = 'en'): string => {
  const names = {
    'sk': {
      'TPP': 'Trvalý pracovný pomer',
      'HALF_TIME': 'Polovičný úväzok',
      'DoVP': 'Dohoda o vykonaní práce',
      'DoPČ': 'Dohoda o pracovnej činnosti',
      'DoBPŠ': 'Dohoda o brigádnickej práci študentov'
    },
    'en': {
      'TPP': 'Permanent Employment',
      'HALF_TIME': 'Part-time Employment',
      'DoVP': 'Work Performance Agreement',
      'DoPČ': 'Work Activity Agreement',
      'DoBPŠ': 'Student Work Agreement'
    }
  };
  
  return names[language][contractType] || contractType;
};

/**
 * Get health insurance company display name
 */
export const getHealthInsuranceDisplayName = (company: HealthInsuranceCompany): string => {
  const names = {
    'VšZP': 'Všeobecná zdravotná poisťovňa',
    'Dôvera': 'Dôvera zdravotná poisťovňa',
    'Union': 'Union zdravotná poisťovňa'
  };
  
  return names[company] || company;
};

/**
 * Check if employee is eligible for specific exemptions
 */
export const getEmployeeExemptions = (employee: Employee) => {
  return {
    studentExemption: employee.is_student && employee.contract_type === 'DoBPŠ',
    pensionerExemption: employee.is_pensioner && ['DoVP', 'DoPČ'].includes(employee.contract_type || ''),
    disabilityBenefits: employee.has_severe_disability,
    taxFreeAllowance: employee.applies_tax_free_allowance,
    childTaxBonus: employee.applies_child_tax_bonus && (employee.children?.length || 0) > 0,
    spouseAllowance: employee.applies_spouse_allowance
  };
};

/**
 * Calculate expected monthly work hours based on contract
 */
export const getExpectedMonthlyHours = (employee: Employee): number => {
  if (!employee.weekly_hours) return 0;
  
  // Average weeks per month
  const weeksPerMonth = 4.33;
  let hours = employee.weekly_hours * weeksPerMonth;
  
  // Adjust for contract type
  switch (employee.contract_type) {
    case 'HALF_TIME':
      hours = hours / 2;
      break;
    case 'DoVP':
    case 'DoPČ':
    case 'DoBPŠ':
      // These are typically project-based, return base calculation
      break;
    default:
      // TPP and others use full calculation
      break;
  }
  
  return Math.round(hours);
};

/**
 * Slovak compliance enablement utilities
 */
export const enableSlovakCompliance = async (employeeId: string, initialData: Partial<Employee> = {}) => {
  const defaultSlovakData = {
    contract_type: 'TPP' as ContractType,
    weekly_hours: 40,
    health_insurance_company: 'VšZP' as HealthInsuranceCompany,
    is_pensioner: false,
    is_student: false,
    is_disabled: false,
    has_severe_disability: false,
    applies_tax_free_allowance: false,
    applies_child_tax_bonus: false,
    applies_spouse_allowance: false,
    has_garnishments: false,
    contract_start_date: new Date().toISOString().split('T')[0],
    ...initialData
  };
  
  return defaultSlovakData;
};